import React, { useMemo } from 'react';
import { Table } from 'src/modules/shared/components/molecules/table/table';
import { type TableColumnProps } from 'src/modules/shared/components/molecules/table/table';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  TagDropdown,
  type TagDropdownOption,
} from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import {
  PiArrowClockwise,
  PiCheckCircle,
  PiDotsThreeVertical,
  PiEye,
  PiPencilSimple,
  PiTrash,
  PiUser,
  PiUsersThree,
  PiXCircle,
} from 'react-icons/pi';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { TCCredentialStatus, type TCCredential } from 'src/api/generated';
import { CREDENTIAL_KIND_LABEL_KEY_BY_KIND } from 'src/modules/settings/utils/credentials.util';

/**
 * Props for CredentialsTable component.
 */
export interface CredentialsTableProps {
  /** Credentials to display. */
  credentials: TCCredential[];
  /** Whether the list is currently loading. */
  loading: boolean;
  /** Whether the list is currently fetching more pages. */
  isLoadingMore: boolean;
  /** Backend user ID for the current user (used for ownership checks). */
  currentUserId: number | null;
  /** Whether the user can update credentials. */
  canUpdate: boolean;
  /** Whether the user can delete credentials. */
  canDelete: boolean;
  /** Whether the user can manage all credentials in the tenant. */
  canManageAll: boolean;
  /** Called when the user wants to edit a credential. */
  onEdit: (credential: TCCredential) => void;
  /** Called when the user wants to rotate a credential secret. */
  onRotateSecret: (credential: TCCredential) => void;
  /** Called when the user wants to reveal a credential secret (super admin only). */
  onRevealSecret: (credential: TCCredential) => void;
  /** Called when the user wants to delete a credential. */
  onDelete: (credential: TCCredential) => void;
  /** Whether the user can reveal credential secrets. */
  canRevealSecret: boolean;
}

/**
 * Returns whether the current user can mutate a credential.
 *
 * @param credential - Credential record.
 * @param currentUserId - Current backend user id.
 * @param canManageAll - Whether the user can manage all tenant credentials.
 * @returns True if the user can mutate the credential.
 */
const canMutateCredential = (
  credential: TCCredential,
  currentUserId: number | null,
  canManageAll: boolean
): boolean => {
  if (canManageAll) return true;
  if (currentUserId === null) return false;
  return credential.ownerUserId === currentUserId;
};

/**
 * CredentialsTable component.
 */
export const CredentialsTable: React.FC<CredentialsTableProps> = ({
  credentials,
  loading,
  isLoadingMore,
  currentUserId,
  canUpdate,
  canDelete,
  canManageAll,
  canRevealSecret,
  onEdit,
  onRotateSecret,
  onRevealSecret,
  onDelete,
}) => {
  const { t } = useI18n();

  const columns: TableColumnProps<TCCredential>[] = useMemo(() => {
    return [
      {
        header: t('settings.credentials.table.name'),
        accessor: 'name',
        render: (_value, credential) => (
          <Text size="small" weight="medium">
            {credential.name}
          </Text>
        ),
      },
      {
        header: t('settings.credentials.table.provider'),
        accessor: 'provider',
        render: (_value, credential) => (
          <Text size="tiny" color="secondary">
            {credential.provider}
          </Text>
        ),
      },
      {
        header: t('settings.credentials.table.kind'),
        accessor: 'kind',
        render: (_value, credential) => (
          <Text size="tiny" color="secondary">
            {t(CREDENTIAL_KIND_LABEL_KEY_BY_KIND[credential.kind])}
          </Text>
        ),
      },
      {
        header: t('settings.credentials.table.status'),
        accessor: 'status',
        render: (_value, credential) => {
          const isActive = credential.status === TCCredentialStatus.ACTIVE;
          const label = isActive
            ? t('settings.credentials.status.active')
            : t('settings.credentials.status.disabled');

          return (
            <div className="flex items-center gap-2" title={label}>
              {isActive ? (
                <PiCheckCircle className="w-4 h-4 text-success-600" />
              ) : (
                <PiXCircle className="w-4 h-4 text-neutral-600" />
              )}
              <Text size="tiny" color="secondary">
                {label}
              </Text>
            </div>
          );
        },
      },
      {
        header: t('settings.credentials.table.shared'),
        accessor: 'sharedWithTenant',
        render: (_value, credential) => {
          const label = credential.sharedWithTenant
            ? t('settings.credentials.yes')
            : t('settings.credentials.no');

          return (
            <div className="flex items-center gap-2" title={label}>
              {credential.sharedWithTenant ? (
                <PiUsersThree className="w-4 h-4 text-secondary-700" />
              ) : (
                <PiUser className="w-4 h-4 text-secondary-700" />
              )}
              <Text size="tiny" color="secondary">
                {label}
              </Text>
            </div>
          );
        },
      },
      {
        header: '',
        accessor: 'actions',
        render: (_value, credential) => {
          const canMutate = canMutateCredential(
            credential,
            currentUserId,
            canManageAll
          );

          const options: TagDropdownOption[] = [
            {
              id: 'edit',
              content: (
                <Text size="small" color="secondary">
                  {t('settings.credentials.actions.edit')}
                </Text>
              ),
              icon: <PiPencilSimple className="w-4 h-4 text-secondary-700" />,
              disabled: !canUpdate || !canMutate,
            },
            {
              id: 'rotate',
              content: (
                <Text size="small" color="secondary">
                  {t('settings.credentials.actions.rotate')}
                </Text>
              ),
              icon: <PiArrowClockwise className="w-4 h-4 text-secondary-700" />,
              disabled: !canUpdate || !canMutate,
            },
            ...(canRevealSecret
              ? [
                  {
                    id: 'reveal',
                    content: (
                      <Text size="small" color="secondary">
                        {t('settings.credentials.actions.revealSecret')}
                      </Text>
                    ),
                    icon: <PiEye className="w-4 h-4 text-secondary-700" />,
                    disabled: false,
                  } satisfies TagDropdownOption,
                ]
              : []),
            {
              id: 'delete',
              content: (
                <Text size="small" color="danger">
                  {t('settings.credentials.actions.delete')}
                </Text>
              ),
              icon: <PiTrash className="w-4 h-4 text-danger-700" />,
              disabled: !canDelete || !canMutate,
            },
          ];

          return (
            <TagDropdown
              content={
                <div className="flex items-center">
                  <PiDotsThreeVertical className="w-4 h-4" />
                </div>
              }
              variant="primary"
              appearance="soft"
              size="sm"
              placement="right-start"
              options={options}
              onOptionSelect={(option) => {
                if (option.id === 'edit') onEdit(credential);
                if (option.id === 'rotate') onRotateSecret(credential);
                if (option.id === 'reveal') onRevealSecret(credential);
                if (option.id === 'delete') onDelete(credential);
              }}
            />
          );
        },
      },
    ];
  }, [
    t,
    canDelete,
    canManageAll,
    canRevealSecret,
    canUpdate,
    currentUserId,
    onDelete,
    onEdit,
    onRevealSecret,
    onRotateSecret,
  ]);

  return (
    <Table<TCCredential>
      variant="neutral"
      columns={columns}
      data={credentials}
      keyExtractor={(credential) =>
        credential.credentialId ??
        `${credential.provider ?? 'credential'}-${credential.name ?? 'unknown'}-${credential.createdAt ?? ''}`
      }
      isLoading={loading && credentials.length === 0}
      isLoadingMore={isLoadingMore}
      emptyStateMessage={t('settings.credentials.table.emptyState.title')}
      emptyStateDescription={t(
        'settings.credentials.table.emptyState.description'
      )}
    />
  );
};
